import { useState, type FocusEvent } from 'react';
import { useDebouncedState } from '@react-hookz/web';
import { T, useGT, Var } from 'gt-react';
import { CheckCircleIcon, WarningIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import type {
  TaskTypeViewSupplierLineMatchColumn,
  TaskTypeViewSupplierLineMatchPlacement,
} from '@erp/shared-types';
import type { SupplierTranslation } from '@erp/rpc';
import { useApplySupplierDocumentLineTranslation } from '@/api/rpc/hooks/accounts-payable/useApplySupplierDocumentLineTranslation';
import { useSearchSupplierTranslations } from '@/api/rpc/hooks/accounts-payable/useSupplierDocumentLines';
import { useCan } from '@/hooks/useCan';
import AssociateProductV2 from '@/components/products/AssociateProductV2';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import type { CorrectionCommitResult } from './useCommitTaskCorrection';
import { useProductVariantDisplayLabel } from './useProductVariantDisplayLabel';
import {
  EMPTY_MARK,
  getValueAtPath,
  readRowArray,
  replaceRow,
  rowObject,
  scalarValuesDiffer,
  setValueAtPath,
} from './taskTypeViewValue';
import {
  formatSupplierLineColumn,
  parseSupplierLineColumn,
  readSupplierDocumentLine,
  readSupplierId,
  remapPick,
  remapSupplierDocumentLine,
  supplierLineNeedsTranslationConfirm,
  supplierLinePreviousMatch,
  supplierLineRowDisplay,
  supplierLineTranslationTerm,
  type SupplierDocumentLineView,
  type SupplierLineRemap,
} from './supplierDocumentLine';

type Commit = (
  next: Record<string, unknown>,
) => void | Promise<CorrectionCommitResult | void>;

type RowCommit = (
  nextRow: Record<string, unknown>,
) => void | Promise<CorrectionCommitResult | void>;

/**
 * Remap UI for rows produced by Match Supplier Document Lines.
 * Confirming a product, or editing a mapped extracted column, writes that
 * row on the task correction. A catalog pick for a known supplier can also
 * update that supplier's translation, after a confirm, when the caller can
 * create supplier product matches.
 */
export function SupplierDocumentLineMatch({
  placement,
  input,
  workingCopy,
  onCommit,
}: {
  placement: TaskTypeViewSupplierLineMatchPlacement;
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  onCommit: Commit;
}) {
  const rows = readRowArray(getValueAtPath(workingCopy, placement.config.path));
  const inputRows = readRowArray(getValueAtPath(input, placement.config.path));
  const supplierId = readSupplierId(workingCopy, placement.config.supplierPath);
  const columns = placement.config.columns;
  const label = placement.config.label?.trim() ? placement.config.label : null;

  function commitRow(index: number, nextRow: Record<string, unknown>) {
    return onCommit(
      setValueAtPath(
        workingCopy,
        placement.config.path,
        replaceRow(rows, index, nextRow),
      ),
    );
  }

  return (
    <div
      className="flex min-w-0 flex-col gap-2"
      data-testid={`task-view-supplier-lines-${placement.id}`}
    >
      {label ? <Label>{label}</Label> : null}
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="empty supplier document line remap list">No lines</T>
        </p>
      ) : (
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>
                <T context="supplier document line table, extracted identifier column">
                  Line
                </T>
              </TableHead>
              {columns.map((column) => (
                <TableHead key={column.id}>{columnLabel(column)}</TableHead>
              ))}
              <TableHead>
                <T context="supplier document line table, matched product column">
                  Product
                </T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <SupplierDocumentLineRow
                key={readSupplierDocumentLine(row, index).index}
                row={rowObject(row)}
                inputRow={rowObject(inputRows[index])}
                line={readSupplierDocumentLine(row, index)}
                columns={columns}
                supplierId={supplierId}
                onCommitRow={(nextRow) => commitRow(index, nextRow)}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function columnLabel(column: TaskTypeViewSupplierLineMatchColumn): string {
  if (column.label?.trim()) return column.label;
  return column.path[column.path.length - 1] ?? '';
}

function SupplierDocumentLineRow({
  row,
  inputRow,
  line,
  columns,
  supplierId,
  onCommitRow,
}: {
  row: Record<string, unknown>;
  inputRow: Record<string, unknown>;
  line: SupplierDocumentLineView;
  columns: readonly TaskTypeViewSupplierLineMatchColumn[];
  supplierId: string | null;
  onCommitRow: RowCommit;
}) {
  const gt = useGT();
  const productLabel = useProductVariantDisplayLabel(line.productVariantId);
  const description =
    line.description ??
    gt('No description', {
      context: 'supplier document line with no extracted description',
    });

  return (
    <TableRow>
      <TableCell className="align-top">
        <SupplierLineRemapPopover
          line={line}
          columns={columns}
          row={row}
          supplierId={supplierId}
          onRemap={(pick) => onCommitRow(remapSupplierDocumentLine(row, pick))}
        />
      </TableCell>
      <TableCell className="align-top">
        <div className="font-medium">{line.sku ?? line.upc ?? EMPTY_MARK}</div>
        <div className="text-muted-foreground">{description}</div>
      </TableCell>
      {columns.map((column) => (
        <TableCell key={column.id} className="align-top">
          <SupplierLineColumnCell
            column={column}
            workingValue={getValueAtPath(row, column.path)}
            inputValue={getValueAtPath(inputRow, column.path)}
            onCommitValue={(value) =>
              onCommitRow(setValueAtPath(row, column.path, value))
            }
          />
        </TableCell>
      ))}
      <TableCell className="align-top">
        <div>{productLabel ?? EMPTY_MARK}</div>
        <div className="text-xs text-muted-foreground">
          {line.matchLayer ??
            gt('unmatched', {
              context: 'supplier document line with no product match',
            })}
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * One mapped extracted value. Blur commits the whole row. The original
 * extraction shows underneath once the working copy differs from it.
 */
function SupplierLineColumnCell({
  column,
  workingValue,
  inputValue,
  onCommitValue,
}: {
  column: TaskTypeViewSupplierLineMatchColumn;
  workingValue: unknown;
  inputValue: unknown;
  onCommitValue: (
    value: unknown,
  ) => void | Promise<CorrectionCommitResult | void>;
}) {
  const serverText = formatSupplierLineColumn(workingValue);
  const [draft, setDraft] = useState<string | null>(null);
  if (draft !== null && draft === serverText) setDraft(null);

  async function handleBlur(event: FocusEvent<HTMLInputElement>) {
    const text = event.currentTarget.value;
    if (text === serverText) {
      setDraft(null);
      return;
    }
    const value = parseSupplierLineColumn(text, workingValue);
    if (!scalarValuesDiffer(value, workingValue)) {
      setDraft(null);
      return;
    }
    setDraft(formatSupplierLineColumn(value));
    const result = await onCommitValue(value);
    if (result === 'conflict' || result === 'failed') setDraft(null);
  }

  return (
    <div className="flex min-w-24 flex-col gap-1">
      <Input
        aria-label={columnLabel(column)}
        value={draft ?? serverText}
        className="h-8 text-sm"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={handleBlur}
      />
      {scalarValuesDiffer(inputValue, workingValue) ? (
        <p
          className="text-xs text-muted-foreground"
          data-testid="task-view-original"
        >
          <T context="secondary text under a task view field showing the extracted original value">
            Original:{' '}
            <Var>{formatSupplierLineColumn(inputValue) || EMPTY_MARK}</Var>
          </T>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Match status is the trigger: green when the row has a product, amber when
 * it does not — the same affordance as a sales-order draft line. The popover
 * pins the extracted row (its mapped columns) on top, then the supplier's own
 * translations, then the catalog picker for a product the supplier has no
 * translation for yet.
 */
function SupplierLineRemapPopover({
  line,
  columns,
  row,
  supplierId,
  onRemap,
}: {
  line: SupplierDocumentLineView;
  columns: readonly TaskTypeViewSupplierLineMatchColumn[];
  row: Record<string, unknown>;
  supplierId: string | null;
  onRemap: (
    pick: SupplierLineRemap,
  ) => void | Promise<CorrectionCommitResult | void>;
}) {
  const gt = useGT();
  const [canCreateTranslation] = useCan(
    Action.Create,
    Subject.SupplierProductMatch,
  );
  const [canUpdateTranslation] = useCan(
    Action.Update,
    Subject.SupplierProductMatch,
  );
  const [canDeleteTranslation] = useCan(
    Action.Delete,
    Subject.SupplierProductMatch,
  );
  const applyTranslation = useApplySupplierDocumentLineTranslation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<SupplierLineRemap | null>(null);
  const [confirmingTranslation, setConfirmingTranslation] = useState(false);
  const [removePreviousMatch, setRemovePreviousMatch] = useState(false);
  const [applyingTranslation, setApplyingTranslation] = useState(false);
  const display = supplierLineRowDisplay(
    line,
    pending,
    gt('No description', {
      context: 'supplier document line with no extracted description',
    }),
  );
  const matched = line.productVariantId != null;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setPending(null);
      setConfirmingTranslation(false);
    }
  }

  async function finish(pick: SupplierLineRemap) {
    const result = await onRemap(pick);
    if (result === 'conflict' || result === 'failed') {
      setPending(pick);
      return;
    }
    setPending(null);
    setConfirmingTranslation(false);
    setOpen(false);
  }

  async function confirm() {
    const pick = remapPick(pending);
    if (!pick) return;
    if (
      supplierLineNeedsTranslationConfirm({
        pick,
        supplierId,
        line,
        canCreateTranslation,
      })
    ) {
      setRemovePreviousMatch(false);
      setConfirmingTranslation(true);
      return;
    }
    await finish(pick);
  }

  async function saveWithTranslation(removePreviousMatch: boolean) {
    const pick = remapPick(pending);
    if (!pick?.productVariantId || !supplierId || applyingTranslation) return;
    const previousSupplierProductId = removePreviousMatch
      ? supplierLinePreviousMatch({ line, pick })
      : null;
    setApplyingTranslation(true);
    try {
      const saved = await applyTranslation.mutateAsync({
        supplierId,
        productVariantId: pick.productVariantId,
        sku: line.sku,
        description: line.description,
        upc: line.upc,
        freeform: line.freeform,
        removePreviousMatch: previousSupplierProductId != null,
        previousSupplierProductId,
      });
      const taught = {
        ...pick,
        supplierProductId: saved.supplierProductId,
      };
      setPending(taught);
      await finish(taught);
    } catch {
      // The hook reports the failure. The confirm stays open so the match
      // can still be saved on this task.
    }
    setApplyingTranslation(false);
  }

  async function saveTaskOnly() {
    const pick = remapPick(pending);
    if (!pick || applyingTranslation) return;
    setApplyingTranslation(true);
    try {
      await finish(pick);
    } catch {
      // Leave the confirm open. The task row was not saved.
    }
    setApplyingTranslation(false);
  }

  return (
    // `modal` so the nested autocomplete's own popover receives keystrokes.
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <SupplierLineStatusTrigger matched={matched} />
      <PopoverContent
        side="right"
        align="start"
        className="max-h-[70vh] w-[560px] overflow-y-auto"
      >
        <SupplierLineRequestStrip
          line={line}
          columns={columns}
          row={row}
          missingDescription={display.headerDescription}
        />
        <SupplierLinePickers
          line={line}
          supplierId={supplierId}
          pending={pending}
          selectedVariantId={display.selectedVariantId}
          selectedProductId={display.selectedProductId}
          onPick={setPending}
        />
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            size="sm"
            disabled={display.saveDisabled || applyingTranslation}
            onClick={() => {
              void confirm();
            }}
          >
            <T context="save a remapped supplier document line onto the task">
              Save Match
            </T>
          </Button>
        </div>
        <SupplierLineTranslationConfirm
          open={confirmingTranslation}
          term={supplierLineTranslationTerm(line)}
          applying={applyingTranslation}
          offerRemovePrevious={
            canUpdateTranslation &&
            canDeleteTranslation &&
            supplierLinePreviousMatch({
              line,
              pick: pending ?? {},
            }) != null
          }
          removePrevious={removePreviousMatch}
          onRemovePreviousChange={setRemovePreviousMatch}
          onOpenChange={setConfirmingTranslation}
          onTaskOnly={() => {
            void saveTaskOnly();
          }}
          onUpdateTranslation={() => {
            void saveWithTranslation(removePreviousMatch);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Catalog picks can teach the supplier. This task only writes the correction.
 * Update Translation writes `ap_supplier_products`, then the correction
 * records that translation.
 */
function SupplierLineTranslationConfirm({
  open,
  term,
  applying,
  offerRemovePrevious,
  removePrevious,
  onRemovePreviousChange,
  onOpenChange,
  onTaskOnly,
  onUpdateTranslation,
}: {
  open: boolean;
  term: string;
  applying: boolean;
  offerRemovePrevious: boolean;
  removePrevious: boolean;
  onRemovePreviousChange: (remove: boolean) => void;
  onOpenChange: (open: boolean) => void;
  onTaskOnly: () => void;
  onUpdateTranslation: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="confirm teaching a supplier a product term from a task">
              Update Supplier Translation?
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            {offerRemovePrevious ? (
              <T context="confirm teaching a supplier a product term when the line already has a match">
                This line already has a supplier match. Teach <Var>{term}</Var>{' '}
                as this product, and remove the previous match if later
                documents should stop using it.
              </T>
            ) : (
              <T context="confirm teaching a supplier a product term from a task">
                Save this match on the task only, or also teach this supplier
                that <Var>{term}</Var> means this product. Later documents will
                match that term here.
              </T>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {offerRemovePrevious ? (
          <div className="flex items-start gap-2">
            <Checkbox
              id="remove-previous-supplier-match"
              checked={removePrevious}
              disabled={applying}
              onCheckedChange={(checked) =>
                onRemovePreviousChange(checked === true)
              }
            />
            <Label
              htmlFor="remove-previous-supplier-match"
              className="cursor-pointer text-sm font-normal"
            >
              <T context="checkbox on the supplier translation confirm, removes the translation this line had">
                Remove the previous match
              </T>
            </Label>
          </div>
        ) : null}
        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={applying}
            onClick={onTaskOnly}
          >
            <T context="save a supplier line match on the task without teaching the supplier">
              This Task Only
            </T>
          </Button>
          <Button
            type="button"
            disabled={applying}
            onClick={onUpdateTranslation}
          >
            <T context="save a supplier line match and teach the supplier the term">
              Update Translation
            </T>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Green when the row has a product, amber when it does not. Opens the picker. */
function SupplierLineStatusTrigger({ matched }: { matched: boolean }) {
  const gt = useGT();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={
              matched
                ? gt('Matched. Change product', {
                    context: 'supplier document line match status, matched',
                  })
                : gt('Not matched. Select product', {
                    context: 'supplier document line match status, unmatched',
                  })
            }
            data-match-status={matched ? 'matched' : 'unmatched'}
            className="mx-auto flex cursor-pointer items-center justify-center rounded p-1 hover:bg-muted"
          >
            {matched ? (
              <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-600" />
            ) : (
              <WarningIcon className="h-4 w-4 flex-shrink-0 text-amber-500" />
            )}
          </button>
        </PopoverTrigger>
      </TooltipTrigger>
      <TooltipContent>
        {matched
          ? gt('Line is matched. Click to change.', {
              context: 'supplier document line match tooltip',
            })
          : gt('Click to match this line to a product.', {
              context: 'supplier document line match tooltip',
            })}
      </TooltipContent>
    </Tooltip>
  );
}

/** The supplier's translations when a supplier is known, then the catalog. */
function SupplierLinePickers({
  line,
  supplierId,
  pending,
  selectedVariantId,
  selectedProductId,
  onPick,
}: {
  line: SupplierDocumentLineView;
  supplierId: string | null;
  pending: SupplierLineRemap | null;
  selectedVariantId: string | undefined;
  selectedProductId: string | undefined;
  onPick: (pick: SupplierLineRemap) => void;
}) {
  return (
    <>
      {supplierId ? (
        <SupplierTranslationPicker
          supplierId={supplierId}
          initialQuery={line.sku ?? ''}
          selectedSupplierProductId={
            pending?.supplierProductId ?? line.supplierProductId
          }
          onPick={onPick}
        />
      ) : null}
      <AssociateProductV2
        selectedProductVariantId={selectedVariantId}
        selectedProductId={selectedProductId}
        onVariantSelected={onPick}
      >
        {supplierId ? (
          <p className="pt-2 text-sm font-medium">
            <T context="heading for the catalog section of the supplier line remap">
              Catalog
            </T>
          </p>
        ) : null}
      </AssociateProductV2>
    </>
  );
}

/**
 * The extracted row, pinned while the person scrolls the pickers. The
 * mapped columns are what the author chose to show on the table; with none,
 * the identifiers stand in.
 */
function SupplierLineRequestStrip({
  line,
  columns,
  row,
  missingDescription,
}: {
  line: SupplierDocumentLineView;
  columns: readonly TaskTypeViewSupplierLineMatchColumn[];
  row: Record<string, unknown>;
  missingDescription: string;
}) {
  const gt = useGT();
  const cells =
    columns.length > 0
      ? columns.map((column) => ({
          id: column.id,
          label: columnLabel(column),
          value: formatSupplierLineColumn(getValueAtPath(row, column.path)),
        }))
      : [
          {
            id: 'sku',
            label: gt('SKU', {
              context: 'supplier line strip, extracted identifier label',
            }),
            value: line.sku ?? line.upc ?? '',
          },
          {
            id: 'description',
            label: gt('Description', {
              context: 'supplier line strip, extracted description label',
            }),
            value: line.description ?? missingDescription,
          },
        ];
  return (
    <div className="sticky top-0 z-10 border-b border-border bg-background pb-2">
      <p className="mb-1 text-sm font-medium">
        <T context="heading for the extracted supplier document line">
          Supplier line
        </T>
      </p>
      <div
        className="grid gap-2 rounded border border-border p-2 text-xs"
        style={{
          gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))`,
        }}
      >
        {cells.map((cell) => (
          <div key={cell.id} className="min-w-0">
            <div className="font-medium text-muted-foreground">
              {cell.label}
            </div>
            <div className="break-words">{cell.value || EMPTY_MARK}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SupplierTranslationPicker({
  supplierId,
  initialQuery,
  selectedSupplierProductId,
  onPick,
}: {
  supplierId: string;
  initialQuery: string;
  selectedSupplierProductId: string | null | undefined;
  onPick: (pick: SupplierLineRemap) => void;
}) {
  const gt = useGT();
  const [query, setQuery] = useDebouncedState(initialQuery, 250);
  const translations = useSearchSupplierTranslations({ supplierId, query });
  const rows = translations.data?.translations ?? [];

  return (
    <div className="flex flex-col gap-2 border-b border-border py-2">
      <p className="text-sm font-medium">
        <T context="heading for the supplier translations section of the supplier line remap">
          This supplier's translations
        </T>
      </p>
      <Input
        defaultValue={initialQuery}
        placeholder={gt('Search supplier SKU, UPC, or description', {
          context: 'placeholder for the supplier translation search',
        })}
        aria-label={gt('Search supplier translations', {
          context: 'aria label for the supplier translation search',
        })}
        className="h-8 text-sm"
        onChange={(event) => setQuery(event.target.value)}
      />
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {translations.isPending ? (
            <T context="supplier translation search loading">Searching…</T>
          ) : (
            <T context="supplier translation search with no results">
              No translations match. Pick from the catalog below.
            </T>
          )}
        </p>
      ) : (
        <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
          {rows.map((translation) => (
            <SupplierTranslationOption
              key={translation.supplierProductId}
              translation={translation}
              selected={
                translation.supplierProductId === selectedSupplierProductId
              }
              onPick={onPick}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function SupplierTranslationOption({
  translation,
  selected,
  onPick,
}: {
  translation: SupplierTranslation;
  selected: boolean;
  onPick: (pick: SupplierLineRemap) => void;
}) {
  const identifier =
    translation.supplierSku ??
    translation.supplierUpc ??
    translation.supplierFreeform[0] ??
    EMPTY_MARK;
  return (
    <li>
      <button
        type="button"
        aria-pressed={selected}
        className={
          selected
            ? 'w-full rounded-md border border-primary bg-primary/5 px-2 py-1 text-left text-sm'
            : 'w-full rounded-md border px-2 py-1 text-left text-sm hover:bg-muted'
        }
        onClick={() =>
          onPick({
            supplierProductId: translation.supplierProductId,
            productId: translation.productId,
            productVariantId: translation.productVariantId,
          })
        }
      >
        <span className="font-medium">{identifier}</span>
        {translation.supplierDescription ? (
          <span className="text-muted-foreground">
            {' '}
            · {translation.supplierDescription}
          </span>
        ) : null}
        <span className="block text-xs text-muted-foreground">
          {translation.variantSku
            ? `${translation.productName} (${translation.variantSku})`
            : translation.productName}
        </span>
      </button>
    </li>
  );
}
